import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
// Import the Firebase modules
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
import { SharedService } from './sharedService';
import { TokenEncryptionService } from './token-encryption.service';
// Import SweetAlert for notifications
import Swal from 'sweetalert2';
// Import additional services
import { ProjectService } from './project.service';
import { MessagingServiceService } from './messaging-service.service';
import { MatDialog } from '@angular/material/dialog';
// Import the WelcomeComponent directly
import { WelcomeComponent } from '../welcome/welcome.component';
// Import the shared auth service
import { AuthService } from './auth.service';
import { Injector } from '@angular/core'; // Import Injector
import { getAnalyticsAttribsWithTouches, getStoredAttributionPayload } from '../utils/attribution.util';

@Injectable({
  providedIn: 'root'
})
export class GoogleAuthService {
  private user = new BehaviorSubject<any>(null);
  private initializationSubject = new BehaviorSubject<boolean>(true);
  private auth: any;
  private isNewGoogleSignInAttempt = false;
  private recentlyLoggedOutManually: boolean = false; // Internal sticky flag
  private manualLogoutTimer: any = null; // Timer for the sticky flag
  unsubscribeAuthObserver: any;
  constructor(private http: HttpClient, private injector: Injector, private tokenEncryptionService: TokenEncryptionService) { }

  async googleServiceInitialized() {
    const firebaseApp = initializeApp(environment.firebase);
    this.auth = getAuth(firebaseApp);

    await this.attachAuthObserver();
  }

  async attachAuthObserver() {
    if (this.unsubscribeAuthObserver) {
      this.unsubscribeAuthObserver(); // Remove existing observer
    }

    this.unsubscribeAuthObserver = await this.auth.onAuthStateChanged(async (firebaseUser: any) => {
      await this.handleAuthStateChanged(firebaseUser);
    });
  }

  async handleAuthStateChanged(firebaseUser: any) {
    const manualLogoutFlagFromStorage = sessionStorage.getItem('manual_logout_in_progress');

    if (manualLogoutFlagFromStorage === 'true') {
      sessionStorage.removeItem('manual_logout_in_progress');
      console.log('GoogleAuthService: Detected manual_logout_in_progress flag from sessionStorage.');
      this.recentlyLoggedOutManually = true;
      if (this.manualLogoutTimer) {
        clearTimeout(this.manualLogoutTimer);
      }
      this.manualLogoutTimer = setTimeout(() => {
        console.log('GoogleAuthService: Resetting internal recentlyLoggedOutManually flag after timeout.');
        this.recentlyLoggedOutManually = false;
        this.manualLogoutTimer = null;
      }, 3000); // Suppress auto-login for 3 seconds post-reload

      if (firebaseUser) {
        console.log('GoogleAuthService: Firebase user detected with manual_logout_in_progress flag. Forcing signOut.');
        // signOut clears this.user and isGoogleLogin localStorage
        this.signOut().catch(err => console.error("GoogleAuthService: Error during signOut triggered by manual_logout_in_progress: ", err));
      }
    }

    if (this.recentlyLoggedOutManually) {
      if (firebaseUser) {
        console.log('GoogleAuthService: Firebase user still detected while recentlyLoggedOutManually is true. Ensuring signOut again.');
        this.signOut().catch(err => console.error("GoogleAuthService: Error during signOut triggered by sticky recentlyLoggedOutManually flag: ", err));
      }
      return; // Prevent login/token processing
    }

    if (firebaseUser) {
      firebaseUser.getIdToken().then((idToken: string) => {
        this.sendTokenToServer(idToken).subscribe((response) => {
          if (response && response.success && response.token) {
            console.log('User is successfully login...');
            const authService = this.injector.get(AuthService); // Get AuthService instance
            authService.handleSuccessfulLogin(response, true, this.isNewGoogleSignInAttempt);
            this.isNewGoogleSignInAttempt = false;
          } else {
            console.error('onAuthStateChanged: Token sent to server, but response was not successful:', response);
            this.user.next(null);
            localStorage.removeItem('isGoogleLogin');
          }
        }, (error) => {
          console.error('onAuthStateChanged: Error sending token to server:', error);
          this.user.next(null);
          localStorage.removeItem('isGoogleLogin');
          // Show error to user if appropriate, e.g., using Swal from here or a shared error handler
          Swal.fire({
            position: 'center',
            icon: 'error',
            title: 'Application Login Failed',
            text: 'Could not sign you into the application after Google Sign-In. Please try again.',
            timer: 3000,
            showConfirmButton: false,
          });
        });
      });
    } else {
      // User is signed out from Firebase
      if (this.recentlyLoggedOutManually) {
        console.log('GoogleAuthService: Firebase reports user signed out while recentlyLoggedOutManually was true. This is good.');
      }
      console.log('GoogleAuthService: Firebase user is null. Normal signed-out state processing.');
      if (localStorage.getItem('isGoogleLogin') === 'true') {
        console.log('onAuthStateChanged: Firebase user signed out, was Google login. Clearing session (already handled by signOut).');
      }
    }
  }

  private sendTokenToServer(idToken: string): Observable<any> {
    const url = `${environment.api.baseUrl}supersee/v1/firebase-login`;
    const attributionPayload = getStoredAttributionPayload(localStorage);
    const payload: any = { id_token: idToken };
    
    if (attributionPayload) {
      const analyticsAttribs = getAnalyticsAttribsWithTouches(attributionPayload);
      payload.first_touch = attributionPayload.first_touch;
      payload.last_touch = attributionPayload.last_touch;
      if (analyticsAttribs) {
        payload.meta = {
          ss_analytics_attribs: analyticsAttribs
        };
      }
    }
    
    return this.http.post(url, payload);
  }

  async signIn(): Promise<any> {
    await this.googleServiceInitialized();
    this.isNewGoogleSignInAttempt = true;
    const provider = new GoogleAuthProvider();

    try {
      const result = await signInWithPopup(this.auth, provider);
      console.log('sign-in successful.', result.user?.displayName);
      return result;
    } catch (error: any) {
      console.error('sign-in error', error);

      // Only show popup error for user-cancel or blocked situations
      if (error.code !== 'auth/cancelled-popup-request') {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: 'Google Sign-In Failed',
          text: this.getReadableErrorMessage(error),
          timer: 3000,
          showConfirmButton: false,
        });
      }

      return error;
    }
  }

  async signOut(): Promise<void> {
    console.log('sign-out called.');
    return signOut(this.auth).then(() => {
      console.log('sign-out successful');
      this.user.next(null);
      localStorage.removeItem('isGoogleLogin'); // Ensure this is cleared here too
    }).catch(error => {
      console.error('GoogleAuthService: Firebase SDK signOut error:', error);
      // Still attempt to clear local state on error
      this.user.next(null);
      localStorage.removeItem('isGoogleLogin');
      throw error; // Re-throw to allow callers to handle it
    });
  }

  getUser(): Observable<any> {
    return this.user.asObservable();
  }

  isSignedIn(): boolean {
    return !!this.user.value;
  }

  isGoogleInitialized(): Observable<boolean> {
    return this.initializationSubject.asObservable();
  }

  /**
   * Convert Firebase error codes to user-friendly messages
   */
  private getReadableErrorMessage(error: any): string {
    const errorCode = error.code;
    switch (errorCode) {
      case 'auth/account-exists-with-different-credential':
        return 'An account already exists with the same email address but different sign-in credentials.';
      case 'auth/popup-blocked':
        return 'The sign-in popup was blocked by your browser. Please allow popups for this website.';
      case 'auth/popup-closed-by-user':
        return 'The sign-in popup was closed before completing the sign-in process.';
      case 'auth/cancelled-popup-request':
        return 'The sign-in process was cancelled.';
      case 'auth/network-request-failed':
        return 'A network error occurred. Please check your internet connection and try again.';
      default:
        return error.message || 'An error occurred during sign-in. Please try again.';
    }
  }
}
